import type { IssueTypes } from '@risksmart-app/trpc/src/services/service.types';
import type {
  InsertChildIssueMutation,
  InsertIssueInput,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertChildIssueDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type ReportPublicIssueTRPCInput = Omit<InsertIssueInput, 'Type'> & {
  Type: IssueTypes;
};

/**
 * Public "report an issue" submit. Under V3 it routes to the dedicated
 * `issue.reportPublic` procedure (authorized on `public_issue_form:read`,
 * parent-less — COI-488); on legacy it uses the same Apollo insert the public
 * form has always used.
 */
export const useReportPublicIssue = createMutationHook({
  name: 'reportPublicIssue',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.issue.reportPublic.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (variables: InsertIssueInput): ReportPublicIssueTRPCInput => ({
    ...variables,
    Type: variables.Type as IssueTypes,
  }),
  invalidate: (trpc) => [
    trpc.frontend.issue.register.queryKey(),
    trpc.frontend.issue.issueById.queryKey(),
    // COI-889: the left-nav badge reads this count family.
    trpc.frontend.issue.issueCount.queryKey(),
  ],
  mapResponse: (result: {
    Id: string;
    SequentialId: number | null;
  }): InsertChildIssueMutation => {
    if (result.SequentialId === null) {
      throw new Error('Missing sequential ID after issue insertion');
    }

    return {
      insertChildIssue: {
        __typename: 'InsertChildIssueOutput',
        Id: result.Id,
        SequentialId: result.SequentialId,
      },
    };
  },
  graphqlDocument: InsertChildIssueDocument,
  graphqlVariables: (variables: InsertIssueInput) => ({ object: variables }),
  evictFields: ['issue', 'issue_aggregate'],
  refetchQueries: [
    namedOperations.Query.getIssueById,
    namedOperations.Query.getIssues,
  ],
});
